import React, { useState, useEffect, useRef } from 'react';

// Joy UI Components
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Input from '@mui/joy/Input';
import Button from '@mui/joy/Button';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Grid from '@mui/joy/Grid';
import Divider from '@mui/joy/Divider';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import Tooltip from '@mui/joy/Tooltip';
import Avatar from '@mui/joy/Avatar';
import Stack from '@mui/joy/Stack';
import Textarea from '@mui/joy/Textarea';
import { v4 as uuidv4 } from 'uuid';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ImageIcon from '@mui/icons-material/Image';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { createNotification } from '../api/notifications';
import ImageUpload from './ImageUpload';
import { getSettings, clearSettingsCache } from '../utils/settings';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';

// Helper function to create rgba color
const alpha = (color, opacity) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

export default function ItemDialog({
  open,
  onClose,
  onSave,        // callback for parent to show snackbar
  type,
  initialData
}) {
  const isEdit = initialData && initialData.isNew === false;
  const { createItem, updateItem } = useInventory();
  const { user } = useAuth();
  const userId = user?.attributes?.sub;
  const initialDataRef = useRef(null);

  // Settings state
  const [availableUnits, setAvailableUnits] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load settings on component mount
  useEffect(() => {
    async function loadSettings() {
      try {
        // Clear cache to ensure fresh settings
        clearSettingsCache();
        const settings = await getSettings();
        
        // Get units for this type
        const customUnits = settings.units?.[type] || [];
        const baseUnits = type === 'PRODUCT-SEED'
          ? ['each','pack','box','gram','kg']
          : type === 'PRODUCT-CLONE'
            ? ['each','plant','pack','box']
            : ['each','roll','sheet','meter','foot','case','bundle','pallet'];
        
        // Combine and deduplicate units
        const allUnits = Array.from(new Set([...baseUnits, ...customUnits]));
        setAvailableUnits(allUnits);
        
        // Get locations
        setAvailableLocations(settings.locations || []);
        
        setSettingsLoaded(true);
      } catch (error) {
        console.error('Error loading settings for dialog:', error);
        
        // Fallback to basic units if settings load fails
        const baseUnits = type === 'PRODUCT-SEED'
          ? ['each','pack','box','gram','kg']
          : type === 'PRODUCT-CLONE'
            ? ['each','plant','pack','box']
            : ['each','roll','sheet','meter','foot','case','bundle','pallet'];
        setAvailableUnits(baseUnits);
        setAvailableLocations([]);
        setSettingsLoaded(true);
      }
    }
    
    loadSettings();
  }, [type]);

  // Get type-specific color
  const getTypeColor = () => {
    switch(type) {
      case 'PRODUCT-SEED': return '#57C84D';
      case 'PRODUCT-CLONE': return '#BC13FE';
      case 'SHIPPING_MATERIAL': return '#ff9800';
      default: return '#2196f3';
    }
  };
  
  const typeColor = getTypeColor();

  // Get type-specific title
  const getTypeTitle = () => {
    if (isEdit && form.name) {
      const itemType = 
        type === 'PRODUCT-SEED' ? 'Seed' : 
        type === 'PRODUCT-CLONE' ? 'Clone' : 
        type === 'SHIPPING_MATERIAL' ? 'Packaging Material' : 'Item';
      return `Edit: ${form.name}`;
    } else {
      switch(type) {
        case 'PRODUCT-SEED': return isEdit ? 'Edit Seed' : 'Add New Seed';
        case 'PRODUCT-CLONE': return isEdit ? 'Edit Clone' : 'Add New Clone';
        case 'SHIPPING_MATERIAL': return isEdit ? 'Edit Packaging Material' : 'Add New Packaging Material';
        default: return isEdit ? 'Edit Item' : 'Add New Item';
      }
    }
  };

  // form template
  const emptyForm = {
    itemType: type,
    itemId: uuidv4(),
    name: '',
    description: '',
    quantity: '',
    unit: 'each',
    location: '',
    reorderThreshold: '',
    packagedCount: '',
    imageUrls: [],
    productRefId: '',
    productCreatedDate: ''
  };
  const [form, setForm] = useState(emptyForm);
  const [hasChanges, setHasChanges] = useState(false);
  const originalPackCount = initialData?.packagedCount || 0;

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlField, setUrlField] = useState('');

  // Track changes from initial data
  useEffect(() => {
    if (initialData) {
      const formData = {
        ...emptyForm,
        ...initialData,
        quantity: initialData.quantity?.toString() || '',
        reorderThreshold: initialData.reorderThreshold?.toString() || '',
        packagedCount: initialData.packagedCount?.toString() || '',
        imageUrls: Array.isArray(initialData.imageUrls)
          ? initialData.imageUrls
          : []
      };
      setForm(formData);
      initialDataRef.current = JSON.stringify(formData);
      setHasChanges(false);
    } else {
      setForm({ ...emptyForm, itemType: type, itemId: uuidv4() });
      initialDataRef.current = null;
      setHasChanges(false);
    }
  }, [initialData, type]);

  // Check for changes
  useEffect(() => {
    if (initialDataRef.current) {
      const currentFormStr = JSON.stringify(form);
      setHasChanges(currentFormStr !== initialDataRef.current);
    } else {
      // For new items, consider having required fields filled as having changes
      setHasChanges(!!form.name && (form.quantity !== '' || form.packagedCount !== ''));
    }
  }, [form]);

  // unified field handler
  const handleField = name => e => {
    let val = e.target.value;
    if (e.target.type === 'number') {
      val = val === '' ? '' : Number(val);
    }
    setForm(f => ({ ...f, [name]: val }));
  };

  // promote selected image to front
  const handleSelectPrimary = idx => {
    setForm(f => {
      const urls = [...f.imageUrls];
      const [primary] = urls.splice(idx, 1);
      return { ...f, imageUrls: [primary, ...urls] };
    });
  };

  // remove image from array
  const handleRemoveImage = idx => {
    setForm(f => {
      const urls = [...f.imageUrls];
      urls.splice(idx, 1);
      return { ...f, imageUrls: urls };
    });
  };

  // Handle resetting form to initial data
  const handleResetForm = () => {
    if (initialDataRef.current) {
      setForm(JSON.parse(initialDataRef.current));
      setHasChanges(false);
    }
  };

  // Prepare unit options
  const unitOptions = availableUnits.map(u => ({
    value: u,
    label: u.charAt(0).toUpperCase() + u.slice(1)
  }));

  // Prepare location options
  const locationOptions = availableLocations.map(loc => ({
    value: loc,
    label: loc
  }));

  const handleSave = async () => {
    // Validate required fields
    if (!form.name || form.name.trim() === '') {
      onSave('Item name is required');
      return;
    }

    let qty = Number(form.quantity) || 0;
    const rt = Number(form.reorderThreshold) || 0;
    const pc = Number(form.packagedCount) || 0;

    // Validate numbers
    if (qty < 0 || rt < 0 || pc < 0) {
      onSave('Quantities cannot be negative');
      return;
    }

    // for seeds: auto‑adjust quantity when pack count rises
    if (isEdit && form.itemType === 'PRODUCT-SEED' && pc > originalPackCount) {
      qty -= (pc - originalPackCount) * 10;
    }

    const payload = {
      ...form,
      name: form.name.trim(),
      description: form.description?.trim() || '',
      quantity: qty,
      reorderThreshold: rt,
      packagedCount: pc
    };

    try {
      if (isEdit) {
        // strip internal props
        const { itemType, itemId, isNew, ...rest } = payload;
        const updates = {};
        Object.entries(rest).forEach(([k, v]) => {
          if (v !== undefined) updates[k] = v;
        });
        await updateItem(itemType, itemId, updates);
      } else {
        await createItem(payload);
      }

      // low‑stock alert, if userId available
      if (userId && qty <= rt && rt > 0) {
        try {
          await createNotification(userId, {
            itemType:    payload.itemType,
            itemId:      payload.itemId,
            message:     `${payload.name} low: ${qty}/${rt}`,
            imageUrl:    (payload.imageUrls && payload.imageUrls.length > 0 && payload.imageUrls[0]) || '',
            name:        payload.name,
            description: payload.description
          });
        } catch (notificationError) {
          console.error('Failed to create low stock notification:', notificationError);
          // Don't fail the save operation for notification errors
        }
      }

      // notify parent to show toast
      onSave(isEdit ? 'Item updated successfully' : 'Item added successfully');
    } catch (err) {
      console.error('Save failed', err);
      // Extract meaningful error message
      const errorMessage = err.message || 'Unknown error occurred';
      onSave(`Save failed: ${errorMessage}`);
    }
  };

  // Add URL via dialog
  const handleAddUrl = () => {
    if (!urlField.trim()) return;
    
    // Validate the URL format
    try {
      new URL(urlField.trim());
      // URL is valid, add it to the imageUrls array
      setForm(f => ({ ...f, imageUrls: [...f.imageUrls, urlField.trim()] }));
      setUrlField('');
      setUrlDialogOpen(false);
    } catch (error) {
      // Invalid URL format
      alert('Please enter a valid URL');
    }
  };

  // Get dialog title
  const dialogTitle = getTypeTitle();

  return (
    <>
      <Modal
        open={open} 
        onClose={onClose} 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          '@media (max-width: 900px)': {
            alignItems: 'flex-end'
          }
        }}
      >
        <ModalDialog
          variant="soft"
          sx={{ 
            width: '100vw',
            height: '100vh',
            maxWidth: 'none',
            maxHeight: 'none',
            m: 0,
            borderRadius: { xs: 0, md: 'xl' },
            overflow: 'auto',
            background: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <ModalClose sx={{ 
              color: 'white',
            position: 'fixed',
            top: 20,
            right: 20,
            zIndex: 1000
          }} />
          
          <Box sx={{ 
            textAlign: 'center', 
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <Typography level="h3" sx={{ color: 'white', mb: 1 }}>
              {dialogTitle}
            </Typography>
            <Typography level="body-md" sx={{ color: 'text.secondary', maxWidth: 400, textAlign: 'center' }}>
              {type === 'edit' 
                ? 'Update the details of your existing item' 
                : 'Create a new item for your inventory'}
            </Typography>
          </Box>

            <Grid container spacing={3}>
              {/* Left Column - Details */}
            <Grid xs={12} md={7}>
                <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ color: 'white', mb: 2 }}>
                    Item Details
                  </Typography>
                <Divider sx={{ mb: 2 }} />

                  {!!form.productRefId && (
                    <Box mb={2}>
                      <Chip 
                        label="Imported Item" 
                        size="small"
                        icon={<CloudDownloadIcon />}
                        sx={{ 
                          bgcolor: alpha(typeColor, 0.15),
                          color: typeColor,
                          mb: 1
                        }} 
                      />
                      <Box sx={{ mb: 1, mt: 1.5 }}>
                      <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                          Imported Seed ID
                        </Typography>
                      <Typography level="body-sm" sx={{ color: 'text.primary' }}>
                          {form.productRefId}
                        </Typography>
                      </Box>
                      {!!form.productCreatedDate && (
                        <Box sx={{ mb: 1 }}>
                        <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.5 }}>
                            Imported Date
                          </Typography>
                        <Typography level="body-sm" sx={{ color: 'text.primary' }}>
                            {form.productCreatedDate}
                          </Typography>
                        </Box>
                      )}
                    <Divider sx={{ my: 2 }} />
                    </Box>
                  )}

                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Name *</FormLabel>
                  <Input
                    value={form.name}
                    onChange={handleField('name')}
                    required
                    sx={{ borderRadius: 'md' }}
                  />
                </FormControl>

                <FormControl sx={{ mb: 2 }}>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={form.description}
                    onChange={handleField('description')}
                    minRows={3}
                    sx={{ borderRadius: 'md' }}
                  />
                </FormControl>
                </Box>

                <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ color: 'white', mb: 2 }}>
                    Inventory Information
                  </Typography>
                <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>
                        {type === 'PRODUCT-SEED'
                            ? 'Unpacked Seed Count'
                            : type === 'PRODUCT-CLONE'
                              ? 'Clones In Progress'
                            : 'Quantity'} *
                      </FormLabel>
                      <Input
                        type="number"
                        value={form.quantity}
                        onChange={handleField('quantity')}
                        required
                        sx={{ borderRadius: 'md' }}
                      />
                    </FormControl>
                    </Grid>
                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Unit</FormLabel>
                        <Select
                          value={form.unit}
                        onChange={(e, value) => setForm(f => ({ ...f, unit: value }))}
                        sx={{ borderRadius: 'md' }}
                        >
                          {unitOptions.map(o => (
                          <Option key={o.value} value={o.value}>
                              {o.label}
                          </Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>Location</FormLabel>
                        <Select
                          value={form.location}
                        onChange={(e, value) => setForm(f => ({ ...f, location: value || '' }))}
                        sx={{ borderRadius: 'md' }}
                        >
                        <Option value="">
                            <em>None</em>
                        </Option>
                          {locationOptions.map(o => (
                          <Option key={o.value} value={o.value}>
                              {o.label}
                          </Option>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                  <Grid xs={12} sm={6}>
                    <FormControl>
                      <FormLabel>
                        {type === 'PRODUCT-SEED'
                            ? 'Reorder Threshold'
                            : type === 'PRODUCT-CLONE'
                              ? 'Replant Threshold'
                            : 'Reorder Threshold'}
                      </FormLabel>
                      <Input
                        type="number"
                        value={form.reorderThreshold}
                        onChange={handleField('reorderThreshold')}
                        placeholder="Alert when stock falls below this value"
                        sx={{ borderRadius: 'md' }}
                      />
                    </FormControl>
                    </Grid>

                    {form.itemType !== 'SHIPPING_MATERIAL' && (
                    <Grid xs={12}>
                      <FormControl>
                        <FormLabel>
                          {type === 'PRODUCT-SEED'
                              ? 'Packaged Seed Packs'
                              : type === 'PRODUCT-CLONE'
                                ? 'Clones Ready'
                              : 'Packaged Count'}
                        </FormLabel>
                        <Input
                          type="number"
                          value={form.packagedCount}
                          onChange={handleField('packagedCount')}
                          sx={{ borderRadius: 'md' }}
                        />
                      </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Grid>

              {/* Right Column - Images */}
            <Grid xs={12} md={5}>
                <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ color: 'white', mb: 2 }}>
                    Images
                  </Typography>
                <Divider sx={{ mb: 2 }} />

                  <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {(form.imageUrls && form.imageUrls.length > 0 && form.imageUrls[0]) ? (
                      <Box
                        component="img"
                        src={form.imageUrls[0]}
                        alt="Primary Image"
                        sx={{
                          width: '100%',
                          height: 200,
                          objectFit: 'cover',
                          borderRadius: 2,
                          mb: 2,
                          border: `2px solid ${typeColor}`,
                          boxShadow: `0 4px 14px ${alpha(typeColor, 0.3)}`
                        }}
                      />
                    ) : (
                    <Sheet
                      variant="soft"
                        sx={{
                          width: '100%',
                          height: 200,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column',
                        borderRadius: 'md',
                          mb: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: `1px dashed rgba(255, 255, 255, 0.2)`
                        }}
                      >
                      <ImageIcon sx={{ fontSize: 50, color: 'rgba(255, 255, 255, 0.4)', mb: 1 }} />
                      <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                          No images added yet
                        </Typography>
                    </Sheet>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
                  {(form.imageUrls && form.imageUrls.length > 0) && form.imageUrls.map((url, i) => (
                      <Box
                        key={i}
                        sx={{
                          position: 'relative',
                          width: 80,
                          height: 80,
                        }}
                      >
                        <Box
                          component="img"
                          src={url}
                          alt=""
                          sx={{
                            width: 80,
                            height: 80,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            borderRadius: 1,
                            border: i === 0 ? `2px solid ${typeColor}` : '2px solid transparent',
                            '&:hover': { borderColor: alpha(typeColor, 0.5) }
                          }}
                          onClick={() => handleSelectPrimary(i)}
                        />
                        {i === 0 && (
                          <Tooltip title="Primary Image">
                            <Avatar
                              sx={{
                                position: 'absolute',
                                top: -8,
                                right: -8,
                                width: 24,
                                height: 24,
                                bgcolor: typeColor,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                              }}
                            >
                              <StarIcon sx={{ fontSize: 14 }} />
                            </Avatar>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(i)}
                          sx={{
                            position: 'absolute',
                            bottom: -8,
                            right: -8,
                            bgcolor: alpha('#f44336', 0.9),
                            color: 'white',
                            width: 24,
                            height: 24,
                            '&:hover': {
                              bgcolor: '#f44336'
                            }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <ImageUpload
                    images={form.imageUrls || []}
                    onChange={urls => setForm(f => ({ ...f, imageUrls: urls }))}
                    />
                    <Button
                      variant="outlined"
                    startDecorator={<LinkIcon />}
                      onClick={() => setUrlDialogOpen(true)}
                    sx={{ borderRadius: 'md' }}
                    >
                      Add URL
                    </Button>
                  </Box>
                </Box>
              </Grid>
            </Grid>

          {/* Actions */}
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button 
            onClick={onClose}
            variant="outlined"
              sx={{ borderRadius: 'md' }}
          >
            Cancel
          </Button>
          
          {isEdit && (
            <Button
              variant="outlined"
                startDecorator={<RestartAltIcon />}
              onClick={handleResetForm}
              disabled={!hasChanges}
                sx={{ borderRadius: 'md' }}
            >
              Clear Changes
            </Button>
          )}
          
          <Button 
              variant="solid"
              startDecorator={<SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges}
              sx={{ borderRadius: 'md' }}
          >
            {isEdit ? 'Save Changes' : 'Add Item'}
          </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      <Modal
        open={urlDialogOpen} 
        onClose={() => setUrlDialogOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <ModalDialog
          variant="soft"
          sx={{
            width: '400px',
            background: 'rgba(0, 0, 0, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 'xl'
          }}
        >
          <ModalClose sx={{ color: 'white' }} />
          
          <Stack spacing={2} sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LinkIcon sx={{ color: '#ff9800' }} />
              <Typography level="h4" sx={{ color: 'white' }}>
              Add Image URL
            </Typography>
            </Stack>
            
            <FormControl>
              <FormLabel>Image URL</FormLabel>
              <Input
            value={urlField}
            onChange={e => setUrlField(e.target.value)}
            placeholder="https://example.com/image.jpg"
                sx={{ borderRadius: 'md' }}
          />
            </FormControl>
            
            <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button 
            onClick={() => setUrlDialogOpen(false)}
                variant="outlined"
                sx={{ borderRadius: 'md' }}
          >
            Cancel
          </Button>
          <Button 
                variant="solid"
            onClick={handleAddUrl}
                sx={{ borderRadius: 'md' }}
          >
            Add
          </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}
